import React, { useState, useMemo } from 'react';
import { CheckSquare, XSquare, Users } from 'lucide-react';

// Format number with commas and 2 decimal places
const formatCurrency = (num) => num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const individualServices = [
    { name: 'CRM with unlimited storage and P&L reporting', price: 19.99 },
    { name: 'Project Management', price: 19.99 },
    { name: 'Notes Management', price: 20.00 },
    { name: 'Chat and Collaboration Tool', price: 24.99 },
    { name: 'A.I Reporting', price: 49.99 },
    { name: 'A.I Chat Assistant', price: 24.99 },
    { name: 'A.I meeting assistant for all meeting platforms', price: 49.99 },
];

const PricingCalculator = () => {
    const [selectedServices, setSelectedServices] = useState(individualServices.map(s => s.name));
    const [teamSize, setTeamSize] = useState(1);
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

    const toggleService = (serviceName) => {
        setSelectedServices(prev =>
            prev.includes(serviceName)
                ? prev.filter(name => name !== serviceName)
                : [...prev, serviceName]
        );
    };

    const oldWayCost = useMemo(() => {
        const monthlyCost = individualServices.reduce((total, service) => {
            if (selectedServices.includes(service.name)) {
                return total + service.price * teamSize;
            }
            return total;
        }, 0);
        return billingCycle === 'yearly' ? monthlyCost * 12 : monthlyCost;
    }, [selectedServices, teamSize, billingCycle]);

    const { ourPrice, tierName, savings } = useMemo(() => {
        let monthlyPrice;
        let tier;
        if (teamSize <= 1) {
            monthlyPrice = 0;
            tier = 'Founder Edition';
        } else if (teamSize <= 4) {
            monthlyPrice = 39.99;
            tier = 'Starter';
        } else {
            monthlyPrice = 79.99;
            tier = 'Pro';
        }
        
        const price = billingCycle === 'yearly' ? monthlyPrice * 12 : monthlyPrice;
        
        return { ourPrice: price, tierName: tier, savings: oldWayCost - price };
    }, [oldWayCost, teamSize, billingCycle]);


    const handleTeamSizeChange = (e) => {
        const value = parseInt(e.target.value, 10);
        setTeamSize(value);
    };

    return (
        <section id="calculator" className="py-24 bg-[#0a0f1c]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="font-display font-bold text-4xl md:text-5xl text-white mb-4">Visualize Your Savings</h2>
                    <p className="text-xl text-gray-400">Stop overpaying for scattered tools. See how much you can save.</p>
                </div>
                
                <div className="flex justify-center items-center gap-4 mb-8">
                    <span className={`font-semibold transition-colors ${billingCycle === 'monthly' ? 'text-[#fbbf24]' : 'text-gray-400'}`}>Monthly</span>
                    <div
                        onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                        className="w-14 h-8 flex items-center bg-[#141b2d] rounded-full p-1 cursor-pointer border border-gray-700"
                    >
                        <div className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform ${billingCycle === 'yearly' ? 'translate-x-6' : ''}`}></div>
                    </div>
                    <span className={`font-semibold transition-colors ${billingCycle === 'yearly' ? 'text-[#fbbf24]' : 'text-gray-400'}`}>Yearly</span>
                </div>

                <div className="glow-box rounded-3xl p-8 lg:p-12">
                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Left side: Service selection */}
                        <div>
                            <h3 className="font-display font-bold text-2xl text-white mb-6">Your Current Tech Stack</h3>
                            <div className="space-y-4">
                                {individualServices.map(service => (
                                    <div
                                        key={service.name}
                                        onClick={() => toggleService(service.name)}
                                        className="flex items-center justify-between p-4 rounded-lg bg-[#141b2d]/60 border border-gray-800 cursor-pointer hover:border-[#fbbf24]/50 transition-all"
                                    >
                                        <div className="flex items-center gap-4">
                                            {selectedServices.includes(service.name) ? (
                                                <CheckSquare className="w-6 h-6 text-[#fbbf24]" />
                                            ) : (
                                                <XSquare className="w-6 h-6 text-gray-600" />
                                            )}
                                            <span className="font-medium text-gray-300">{service.name}</span>
                                        </div>
                                        <span className="text-gray-400 font-mono">${service.price.toFixed(2)}/mo</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8">
                                <label htmlFor="teamSize" className="flex items-center gap-3 text-xl font-bold text-white mb-4">
                                    <Users className="w-6 h-6 text-[#fbbf24]" />
                                    <span>Team Size</span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <input
                                        id="teamSize"
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={teamSize}
                                        onChange={handleTeamSizeChange}
                                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer range-thumb"
                                        style={{ accentColor: '#fbbf24' }}
                                    />
                                    <span className="text-white font-bold text-2xl w-12 text-center">{teamSize}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right side: Cost comparison */}
                        <div className="bg-[#141b2d]/50 border border-gray-800 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
                             <h3 className="font-display font-bold text-2xl text-white mb-4">Your Estimated Costs</h3>
                            <div className="w-full">
                                <div className="mb-6">
                                    <p className="text-gray-400 text-lg">"The Old Way" {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} Cost</p>
                                    <p className="text-5xl font-bold text-red-500 line-through decoration-2">
                                        ${formatCurrency(oldWayCost)}
                                    </p>
                                </div>

                                <div className="mb-8 p-6 glow-box rounded-xl">
                                    <p className="text-gray-300 text-lg">CloudRealtor.io <span className="font-bold text-[#fbbf24]">{tierName}</span> Plan</p>
                                    <p className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-[#fbbf24] my-2">
                                        ${formatCurrency(ourPrice)}
                                    </p>
                                    <p className="text-gray-400 text-sm">
                                        {
                                            teamSize === 1 ? 'for you.' :
                                            teamSize <= 4 ? `for your team of ${teamSize}. Includes 1 pro seat and ${teamSize-1} free seats.` :
                                            `for your team of ${teamSize}. Includes 1 pro seat and ${teamSize-1} free seats.`
                                        }
                                    </p>
                                </div>
                                
                                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                                    <p className="text-green-400 text-lg font-semibold">Your {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} Savings</p>
                                    <p className="text-5xl font-bold text-green-400">
                                        ${savings > 0 ? formatCurrency(savings) : '0.00'}
                                    </p>
                                    <p className="text-green-300/80 mt-2">Plus, get access to a fully integrated platform designed for real estate agents.</p>
                                </div>

                                <div className="mt-8 text-gray-300 text-lg">
                                  It's a no-brainer. Stop juggling tools and start closing deals.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PricingCalculator;
